import { useEffect, useMemo, useState } from 'react';
import { Callout, OriginTag, Panel, Tile } from '../components/Ui';
import { useStore } from '../data/store';
import { loadRoomSweep } from '../data/source';
import { num, percent, stamp } from '../lib/format';
import type { RoomSweepReport } from '../lib/types';

export function Rooms() {
  const store = useStore();
  const [report, setReport] = useState<RoomSweepReport | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (store.server === null) return;
    let alive = true;
    setLoading(true);
    void loadRoomSweep(store.server.online).then((result) => {
      if (!alive) return;
      setReport(result.value);
      setFailure(result.fallbackReason);
      setLoading(false);
    });
    return () => { alive = false; };
  }, [store.server]);

  const rows = useMemo(
    () => [...(report?.modules ?? [])].sort((a, b) => a.chance - b.chance || a.id.localeCompare(b.id)),
    [report],
  );
  const impossible = rows.filter((room) => !room.placeable);
  const missed = rows.filter((room) => room.placeable && room.runsWithModule === 0);

  return (
    <div className="page">
      <div className="page-head">
        <h1>Комнаты</h1>
        <p>
          Частота каждого RoomModuleDef в настоящей грамматике уровня: геометрия, роли,
          сокеты, веса и лимиты экземпляров участвуют в выборе.
        </p>
        {report && <OriginTag origin="server" />}
      </div>

      {loading && <div className="empty">загрузка частот комнат…</div>}
      {!loading && report === null && (
        <Callout tone="hard" title="Нет свежего прогона комнат">
          <p>{failure ?? 'Для этого раздела нужен запущенный локальный сервер.'}</p>
          <pre className="cmd">Tools/playground.sh --runs 1000</pre>
        </Callout>
      )}

      {report && (
        <>
          <div className="tiles">
            <Tile label="Прогонов" value={num(report.runs)} note={`seed ${num(report.firstSeed)}…${num(report.firstSeed + report.runs - 1)}`} />
            <Tile label="Модулей в пуле" value={num(rows.length)} note={report.scene} />
            <Tile
              label="Не выпали ни разу"
              value={num(missed.length)}
              tone={missed.length === 0 ? 'good' : 'bad'}
              note="среди модулей, которые грамматика умеет ставить"
            />
            <Tile
              label="Исключены правилами"
              value={num(impossible.length)}
              tone={impossible.length === 0 ? 'good' : 'warn'}
              note="вес, роль или несовместимый сокет"
            />
            <Tile
              label="Fallback"
              value={num(report.fallbackRuns)}
              tone={report.fallbackRuns === 0 ? 'good' : 'warn'}
              note={percent(report.fallbackRuns / report.runs)}
            />
          </div>

          {missed.length > 0 && (
            <Callout tone="hard" title="Грамматика допускает комнату, но она не встретилась">
              <p>{missed.map((room) => room.id).join(', ')}</p>
            </Callout>
          )}

          <Panel
            title={`Шансы выпадения комнат — ${num(report.runs)} прогонов`}
            flush
            aside={<span className="dim">снято {stamp(report.generatedAtUtc)} · {report.configName}@{report.configHash}</span>}
          >
            <div className="scroll">
              <table>
                <thead>
                  <tr>
                    <th>Комната</th>
                    <th>Роли</th>
                    <th className="n">Вес</th>
                    <th className="n">Дверей</th>
                    <th className="n">Шанс уровня</th>
                    <th className="n">Уровней</th>
                    <th className="n">Появлений</th>
                    <th className="n">Появлений / 1000</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((room) => {
                    const zero = room.runsWithModule === 0;
                    return (
                      <tr key={room.id} title={room.exclusionReason || undefined}>
                        <td className="mono">{room.id}</td>
                        <td>{room.roles}</td>
                        <td className={room.weight === 0 ? 'n bad' : 'n'}>{num(room.weight)}</td>
                        <td className="n">{num(room.sockets)}</td>
                        <td className={zero ? 'n bad' : 'n good'}>{percent(room.chance)}</td>
                        <td className={zero ? 'n bad' : 'n'}>{num(room.runsWithModule)} / {num(report.runs)}</td>
                        <td className="n">{num(room.appearances)}</td>
                        <td className="n">{num(room.appearancesPer1000Runs, 1)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Panel>

          {impossible.length > 0 && (
            <Panel title="Почему некоторые комнаты исключены">
              {impossible.map((room) => (
                <p key={room.id} style={{ marginTop: 0 }}>
                  <b className="mono">{room.id}</b> — {room.exclusionReason}
                </p>
              ))}
            </Panel>
          )}
        </>
      )}
    </div>
  );
}
